import React, { useEffect, useState } from 'react';
import { Alert, Box, Button, Grid2, LinearProgress, Typography } from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import {
    convertWaitlistToBooking as convertWaitlistToBookingApi,
    deleteWaitlistSubscription as deleteWaitlistSubscriptionApi,
    sendBulkWaitlistReminder,
    useGetWaitlistFilterOptions,
    useGetWaitlistSubscriptions,
} from '@openapi/waitlist-subscriptions/waitlist-subscriptions';
import useNotify from '../../hooks/useNotify';
import useSorting from '../../hooks/useSorting';
import { TABLE_ROW_COUNT_DEFAULT } from '../../helpers/constants';
import PageWithTitle from '../../components/atom/PageWithTitle';
import WaitlistSubscriptionsTable from './components/WaitlistSubscriptionsTable';
import WaitlistFilters from './components/WaitlistFilters';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';

const EMPTY_SUBSCRIPTIONS = [];

const WaitlistPage = () => {
    const { t } = useTranslation();
    const { notifyError, notifySuccess, notifyInfo } = useNotify();
    const [searchParams] = useSearchParams();
    const [{ page, limit }, setParams] = useState({ page: 0, limit: TABLE_ROW_COUNT_DEFAULT });
    const [selectedShowId, setSelectedShowId] = useState(searchParams.get('showId') || '');
    const [selectedTimeslotId, setSelectedTimeslotId] = useState(
        searchParams.get('timeslotId') || '',
    );
    const [search, setSearch] = useState('');
    const {
        sort: columnSortField,
        sortDir: columnSortDir,
        setSorting: setColumnSorting,
    } = useSorting(['', 'ASC']);
    const [onlyFulfillable, setOnlyFulfillable] = useState(
        searchParams.get('onlyFulfillable') === 'true',
    );
    const [onlyNeverNotified, setOnlyNeverNotified] = useState(false);
    const [selectedIds, setSelectedIds] = useState([]);
    const [sendingReminderIds, setSendingReminderIds] = useState([]);
    const [sendingBulkReminder, setSendingBulkReminder] = useState(false);
    const [deletingId, setDeletingId] = useState(null);
    const [convertingId, setConvertingId] = useState(null);
    const navigate = useNavigate();

    const resetSelectionAndPage = () => {
        setSelectedIds([]);
        setParams((prev) => ({ ...prev, page: 0 }));
    };

    const {
        data: filterOptionsData,
        isLoading: loadingFilterOptions,
        refetch: refetchFilterOptions,
    } = useGetWaitlistFilterOptions({
        query: { onError: notifyError },
    });

    const shows = filterOptionsData?.shows || [];
    const timeslotsByShowId = filterOptionsData?.timeslotsByShowId || {};
    const timeslots = timeslotsByShowId[String(selectedShowId)] || [];

    useEffect(() => {
        if (shows.length === 0) return;

        let nextShowId = selectedShowId;
        const selectedShowStillAvailable = shows.some(
            (show) => String(show.id) === String(selectedShowId),
        );
        if (!selectedShowStillAvailable) {
            nextShowId = String(shows[0].id);
        }

        const showTimeslots = timeslotsByShowId[String(nextShowId)] || [];
        const timeslotStillAvailable =
            !selectedTimeslotId ||
            showTimeslots.some((ts) => String(ts.id) === String(selectedTimeslotId));
        const nextTimeslotId = timeslotStillAvailable ? selectedTimeslotId : '';

        if (nextShowId !== selectedShowId || nextTimeslotId !== selectedTimeslotId) {
            setSelectedShowId(nextShowId);
            setSelectedTimeslotId(nextTimeslotId);
            setSelectedIds([]);
            setParams((prev) => ({ ...prev, page: 0 }));
        }
    }, [selectedShowId, selectedTimeslotId, shows, timeslotsByShowId]);

    const queryParams = {
        showId: selectedShowId ? Number(selectedShowId) : undefined,
        timeslotId: selectedTimeslotId ? Number(selectedTimeslotId) : undefined,
        search: search || undefined,
        page,
        pageSize: limit,
        sort: columnSortField || undefined,
        sortDir: columnSortField ? columnSortDir : undefined,
        onlyFulfillable: onlyFulfillable || undefined,
        onlyNeverNotified: onlyNeverNotified || undefined,
    };

    const {
        data: subscriptionsData,
        isLoading: loadingSubscriptions,
        isFetching,
        refetch: refetchSubscriptions,
    } = useGetWaitlistSubscriptions(queryParams, {
        query: {
            enabled: !!selectedShowId,
        },
    });

    const subscriptions = subscriptionsData?.subscriptions || EMPTY_SUBSCRIPTIONS;
    const total = subscriptionsData?.total || 0;
    const isSubscriptionsInitialLoading =
        !subscriptionsData && (loadingSubscriptions || isFetching);
    const visibleSelectedIds = selectedIds.filter((id) => subscriptions.some((s) => s.id === id));

    useEffect(() => {
        if (page > 0 && subscriptions.length === 0) {
            setParams((prev) => ({ ...prev, page: Math.max(0, prev.page - 1) }));
        }
    }, [page, subscriptions.length]);

    const handleSendReminder = async (id) => {
        setSendingReminderIds((current) => [...current, id]);
        try {
            const body = await sendBulkWaitlistReminder({ ids: [id] });
            if (body.sentCount > 0) {
                notifySuccess(t('waitlist.notify.reminderSent'));
            } else if (body.skippedCount > 0) {
                notifyInfo(t('waitlist.notify.notFulfillable'));
            } else if (body.notFoundCount > 0) {
                notifyInfo(t('waitlist.notify.notFound'));
            } else if (body.failedCount > 0) {
                notifyError(t('waitlist.notify.failedReminder'));
            }
            await refetchSubscriptions();
        } catch (error) {
            notifyError(error);
        } finally {
            setSendingReminderIds((current) => current.filter((entry) => entry !== id));
        }
    };

    const handleDeleteSubscription = async (id) => {
        setDeletingId(id);
        try {
            await deleteWaitlistSubscriptionApi(id);
            notifySuccess(t('waitlist.notify.deleted'));
            await refetchSubscriptions();
            await refetchFilterOptions();
            setSelectedIds((current) => current.filter((entry) => entry !== id));
        } catch (error) {
            notifyError(error);
        } finally {
            setDeletingId(null);
        }
    };

    const handleConvertToBooking = async (id) => {
        setConvertingId(id);
        try {
            const result = await convertWaitlistToBookingApi(id);
            notifySuccess(t('waitlist.notify.convertSuccess'));
            await refetchSubscriptions();
            await refetchFilterOptions();
            setSelectedIds((current) => current.filter((entry) => entry !== id));
            navigate(result.bookingEditUrl);
        } catch (error) {
            notifyError(error);
        } finally {
            setConvertingId(null);
        }
    };

    const handleBulkReminder = async () => {
        if (visibleSelectedIds.length === 0 || sendingBulkReminder) return;

        setSendingBulkReminder(true);
        try {
            const body = await sendBulkWaitlistReminder({ ids: visibleSelectedIds });
            const details = [];
            if (body.skippedCount) {
                details.push(t('waitlist.notify.bulkSkipped', { count: body.skippedCount }));
            }
            if (body.notFoundCount) {
                details.push(t('waitlist.notify.bulkNotFound', { count: body.notFoundCount }));
            }
            if (body.failedCount) {
                details.push(t('waitlist.notify.bulkFailed', { count: body.failedCount }));
            }
            const detailsMsg = details.length > 0 ? details.join(', ') : null;

            if ((body.sentCount || 0) > 0) {
                notifySuccess(t('waitlist.notify.bulkSent', { count: body.sentCount }));
                if (detailsMsg) notifyInfo(detailsMsg);
            } else if (detailsMsg) {
                if ((body.failedCount || 0) > 0) {
                    notifyError(detailsMsg);
                } else {
                    notifyInfo(detailsMsg);
                }
            }

            await refetchSubscriptions();

            if (body.results) {
                const sentIds = new Set(
                    body.results
                        .filter((entry) => entry.status === 'sent')
                        .map((entry) => entry.id),
                );
                if (sentIds.size > 0) {
                    setSelectedIds((current) => current.filter((id) => !sentIds.has(id)));
                }
            }
        } catch (error) {
            notifyError(error);
        } finally {
            setSendingBulkReminder(false);
        }
    };

    const handleColumnSort = (nextSortField) => {
        setColumnSorting(nextSortField);
        resetSelectionAndPage();
    };

    return (
        <PageWithTitle
            title={t('waitlist.pageTitle')}
            headline={[
                <Button
                    key="send-bulk-reminder"
                    variant="contained"
                    color="secondary"
                    startIcon={<SendIcon />}
                    disabled={visibleSelectedIds.length === 0 || sendingBulkReminder}
                    onClick={handleBulkReminder}
                    sx={{ textTransform: 'none' }}
                >
                    {t('waitlist.actions.sendBulkReminder')}
                </Button>,
            ]}
        >
            <WaitlistFilters
                shows={shows}
                timeslots={timeslots}
                selectedShowId={selectedShowId}
                selectedTimeslotId={selectedTimeslotId}
                onShowChange={(event) => {
                    setSelectedShowId(String(event.target.value));
                    setSelectedTimeslotId('');
                    resetSelectionAndPage();
                }}
                onTimeslotChange={(event) => {
                    setSelectedTimeslotId(String(event.target.value));
                    resetSelectionAndPage();
                }}
                search={search}
                setSearch={setSearch}
                setParams={setParams}
                limit={limit}
                loadingFilterOptions={loadingFilterOptions}
                onlyFulfillable={onlyFulfillable}
                onFulfillableChange={(event) => {
                    setOnlyFulfillable(event.target.checked);
                    resetSelectionAndPage();
                }}
                onlyNeverNotified={onlyNeverNotified}
                onNeverNotifiedChange={(event) => {
                    setOnlyNeverNotified(event.target.checked);
                    resetSelectionAndPage();
                }}
            />

            {sendingBulkReminder && (
                <Grid2 size={{ xs: 12 }}>
                    <Box sx={{ mt: 0, mb: 1 }}>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                            {t('waitlist.sendingReminders')}
                        </Typography>
                        <LinearProgress />
                    </Box>
                </Grid2>
            )}

            {!selectedShowId ? (
                <Grid2 size={{ xs: 12 }}>
                    <Alert severity="info">{t('waitlist.selectShow')}</Alert>
                </Grid2>
            ) : (
                <WaitlistSubscriptionsTable
                    rows={subscriptions}
                    total={total}
                    page={page}
                    limit={limit}
                    setParams={setParams}
                    loading={isSubscriptionsInitialLoading || loadingFilterOptions}
                    selectedIds={selectedIds}
                    setSelectedIds={setSelectedIds}
                    onSendReminder={handleSendReminder}
                    onDelete={handleDeleteSubscription}
                    onConvertToBooking={handleConvertToBooking}
                    sendingReminderIds={sendingReminderIds}
                    deletingId={deletingId}
                    convertingId={convertingId}
                    sort={columnSortField}
                    sortDir={columnSortDir}
                    onSort={handleColumnSort}
                    hasActiveFilters={!!search.trim() || onlyFulfillable || onlyNeverNotified}
                    showTimeslotColumn={!selectedTimeslotId}
                />
            )}
        </PageWithTitle>
    );
};

export default WaitlistPage;
